import { IPermissionRepository } from './IPermissionRepository';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import { ApiResponseModel } from 'models/ApiResponseModel';
import Logger from 'utilities/log';
import NewPermissionModel, { FormModel, PermissionGetPayloadModel, PermissionModel, PermissionPayloadModel, PermissionRoleModel, PermissionUserModel } from 'models/auth/PermissionModels';
import { PaginationModel } from 'models/PaginationModel';

@injectable()
class PermissionRepository implements IPermissionRepository {

    @inject(Symbol.for("BaseApiProvider_5080")) private _provider?: BaseApiProvider;

    public async GetAllRolesList(Title: string): Promise<PermissionRoleModel[]> {
        try {
            const url = `Permission/info/GetAllRolesList`;
            const formData = new FormData();
            formData.append('Title', Title.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetAllRolesList');
                return [];
            }

            return response["data"].map((js: any) => PermissionRoleModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get GetAllRolesList: ${error}`);
            throw new Error(`Failed to get GetAllRolesList: ${error}`);
        }
    }

    public async GetUserList(RoleId: number): Promise<PermissionUserModel[]> {
        try {
            const url = `Permission/info/GetUserList`;
            const formData = new FormData();
            formData.append('RoleId', RoleId.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetUserList');
                return [];
            }

            return response["data"].map((js: any) => PermissionUserModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get GetUserList: ${error}`);
            throw new Error(`Failed to get GetUserList: ${error}`);
        }
    }

    public async GetFormPermission(RoleId: number, UserId: number, Title: string,): Promise<PermissionModel[]> {
        try {
            const url = `Permission/info/GetFormPermission`;
            const formData = new FormData();
            formData.append('RoleId', RoleId.toString());
            formData.append('UserId', UserId.toString());
            formData.append('Title', Title.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetFormPermission');
                return [];
            }

            return response["data"].map((js: any) => PermissionModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get GetFormPermission: ${error}`);
            throw new Error(`Failed to get GetFormPermission: ${error}`);
        }
    }

    public async GetEelementPermission(RoleId: number, UserId: number, FormId: number,): Promise<PermissionModel[]> {
        try {
            const url = `Permission/info/GetEelementPermission`;
            const formData = new FormData();
            formData.append('RoleId', RoleId.toString());
            formData.append('UserId', UserId.toString());
            formData.append('FormId', FormId.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetEelementPermission');
                return [];
            }

            return response["data"].map((js: any) => PermissionModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get GetEelementPermission: ${error}`);
            throw new Error(`Failed to get GetEelementPermission: ${error}`);
        }
    }

    public async GetUserFormPermission(FormId?: number): Promise<PermissionModel[]> {
        try {
            const url = `Api/PermissionInfo/GetUserFormPermission`;
            const formData = new FormData();
            formData.append('FormId', FormId ? FormId.toString() : "0");
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetUserFormPermission');
                return [];
            }

            return response["data"].map((js: any) => PermissionModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get GetUserFormPermission: ${error}`);
            throw new Error(`Failed to get GetUserFormPermission: ${error}`);
        }
    }

  
    public async SetFormPermission(params: PermissionPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/PermissionAction/SetFormPermission`;
            const formData = new FormData();
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value.toString());
            });
            const response = await this._provider!.postForm(url, formData);
            return ApiResponseModel.fromJson(response);
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }


//#region NEW


        public async GetFormsInfo(hassAccess: number ): Promise<FormModel[]> {
            try {
                const url = `Api/PermissionInfo/GetFormsInfo`;
                const formData = new FormData();
                formData.append('hassAccess', hassAccess.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                if (!res.status) {
                    Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetFormsInfo');
                    return [];
                }

                return response["data"].map((js: any) => FormModel.fromJson(js));
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to get GetFormsInfo: ${error}`);
                throw new Error(`Failed to get GetFormsInfo: ${error}`);
            }
        }
        public async GetAllUserFormPermissionInfo(params: PermissionGetPayloadModel): Promise<{
            rows: PermissionModel[];
            pagination: PaginationModel;
        } | null> {
            try {
                const url = `Api/PermissionInfo/GetAllUserFormPermissionInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
    
                if (!res.status) {
                    Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get UserFormPermission list');
                    return null;
                }
    
                return {
                    rows: response["data"]["rows"].map((js: any) => NewPermissionModel.fromJson(js)),
                    pagination: PaginationModel.fromJson(response["data"])
                };
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to get smslogs list: ${error}`);
                throw new Error(`Failed to get smslogs list: ${error}`);
            }
        }
        public async GetUserFormPermissionInfo(params: PermissionGetPayloadModel): Promise<PermissionModel| null>{
            try {
                const url = `Api/PermissionInfo/GetUserFormPermissionInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
    
                if (!res.status) {
                    Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get UserFormPermission info');
                    return null;
                }
                return NewPermissionModel.fromJson(response["data"]);
    
            } catch (error) {
                if (error instanceof Error) {
                    Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                    throw new Error(`Api failed: ${error.message}`);
                } else {
                    Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                    throw new Error('Api failed: Unknown error');
                }
            }
        }
//#endregion NEW
}

export default PermissionRepository;
